import React, { useState } from "react";
import {
	Box,
	Card,
	CardContent,
	Typography,
	Divider,
	CircularProgress,
	Alert,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	Link,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Button
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
	AttachFile as AttachFileIcon,
	ExpandMore as ExpandMoreIcon,
	History as HistoryIcon,
	Download as DownloadIcon
} from "@mui/icons-material";
import { RichTextReadOnly } from "mui-tiptap";
import { getExtensions } from "../../../components/UI/HtmlEditor";
import { useTranslation } from "react-i18next";
import { useProjectFreelancerProposal } from "../../../hooks/useProjectFreelancerProposal";
import { useProject } from "../../../hooks/useProject";
import ProposalWorkflowActions from "../../../components/proposal/ProposalWorkflowActions";
import ProposalStatusHistory from "../../../components/proposal/ProposalStatusHistory";
import ProposalConversationThread from "../../../components/ProposalConversationThread";
import { useAuthProvider } from "../../../auth/hooks/auth-provider";
import { UserRole } from "../../../models/UserRole";
import { UserType } from "../../../models/UserType";
import { useQueryClient } from "@tanstack/react-query";
import { useProposalConversations } from "../../../hooks/useProposalConversations";
import { zoullooColors } from "../../../theme";
import { FreelancerProposalStatus } from "../../../models/ProposalWorkflow";
import { getPennylaneQuotePdfUrl } from "../../../api/freelancer-proposals-api";

interface FreelancerProposalDetailsProps {
	projectId: number;
	freelancerProfileId: number;
	enabled: boolean;
}

const FreelancerProposalDetails: React.FC<FreelancerProposalDetailsProps> = ({
	projectId,
	freelancerProfileId,
	enabled
}) => {
	const { t } = useTranslation();
	const { user } = useAuthProvider();
	const queryClient = useQueryClient();

	// Check if user is CSM or Admin
	const isCSMorAdmin = user?.roles?.some(
		role => role === UserRole.CSM || role === UserRole.Admin
	);

	const {
		data: proposal,
		isLoading,
		error
	} = useProjectFreelancerProposal(projectId, freelancerProfileId, enabled);

	const { data: project } = useProject(projectId);

	// Load conversations for this proposal
	const {
		data: conversations = [],
		isLoading: isLoadingConversations,
		error: conversationsError
	} = useProposalConversations(proposal?.id || 0, !!proposal?.id);

	// Pennylane quote PDF download state
	const [isDownloadingQuote, setIsDownloadingQuote] = useState(false);

	const handleDownloadPennylaneQuote = async () => {
		if (!proposal?.id) return;

		setIsDownloadingQuote(true);
		try {
			const pdfUrl = await getPennylaneQuotePdfUrl(proposal.id);
			// Open the PDF in a new tab
			window.open(pdfUrl, "_blank");
		} catch (error) {
			console.error("Error downloading Pennylane quote:", error);
		} finally {
			setIsDownloadingQuote(false);
		}
	};

	if (!enabled) {
		return null;
	}

	if (isLoading) {
		return (
			<Box display="flex" justifyContent="center" p={3}>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity="error" sx={{ m: 2 }}>
				{t("project.freelancerDetails.proposalError")}
			</Alert>
		);
	}

	if (!proposal) {
		return (
			<Alert severity="info" sx={{ m: 2 }}>
				{t("project.talentHasNoProposal")}
			</Alert>
		);
	}

	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat("fr-FR", {
			style: "currency",
			currency: "EUR",
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		}).format(amount);
	};

	const calculateTotalWithFees = (): number => {
		const tasksTotal = proposal?.totalBudget || 0;
		const feePercentage = project?.feePercentage || 0;

		// Protection contre la division par zéro ou valeurs invalides
		if (feePercentage >= 100 || feePercentage < 0) {
			return tasksTotal;
		}

		// Formule : Prix total = TJM / (1 - taux_marge)
		return tasksTotal / (1 - feePercentage / 100);
	};

	const calculateZoullooFees = (): number => {
		// Marge = Prix total - TJM
		return calculateTotalWithFees() - (proposal?.totalBudget || 0);
	};

	const calculateExternalCostsWithFees = (): number => {
		const externalCosts = proposal?.totalExternalCosts || 0;
		const feePercentage = project?.externalCostsFeePercentage || 0;
		return externalCosts * (1 + feePercentage / 100);
	};

	const calculateExternalCostsFees = (): number => {
		return calculateExternalCostsWithFees() - (proposal?.totalExternalCosts || 0);
	};

	const calculateExpensesWithFees = (): number => {
		const expenses = proposal?.totalExpenses || 0;
		const feePercentage = project?.expensesFeePercentage || 0;
		return expenses * (1 + feePercentage / 100);
	};

	const calculateExpensesFees = (): number => {
		return calculateExpensesWithFees() - (proposal?.totalExpenses || 0);
	};

	const handleActionComplete = () => {
		// Refresh the proposal data and related queries
		queryClient.invalidateQueries({
			queryKey: ["project-freelancer-proposal", projectId, freelancerProfileId]
		});
		queryClient.invalidateQueries({ queryKey: ["project", projectId] });
		queryClient.invalidateQueries({
			queryKey: ["proposal-available-actions", proposal.id]
		});
	};

	const hasRole = (role: string) => user?.roles?.includes(role) ?? false;
	const canManageWorkflow =
		hasRole(UserRole.CSM) ||
		hasRole(UserRole.Admin) ||
		hasRole(UserRole.TAM) ||
		user?.type === UserType.Client ||
		user?.type === UserType.Freelancer;

	return (
		<Box sx={{ p: 2 }}>
			{/* Status */}
			<Card sx={{ mb: 3 }}>
				<CardContent>
					<Box mb={2}>
						<Typography variant="h6">
							{t("project.freelancerDetails.proposalOverview")}
						</Typography>
					</Box>

					<Grid container spacing={2}>
						<Grid size={{ xs: 12, md: 6 }}>
							<Typography variant="body2" color="text.secondary">
								{t("freelancerProposal.estimatedStartDate")}
							</Typography>
							<Typography variant="body1">
								{new Date(proposal.estimatedStartDate).toLocaleDateString("fr-FR") ===
								"01/01/1970"
									? "-"
									: new Date(proposal.estimatedStartDate).toLocaleDateString("fr-FR")}
							</Typography>
						</Grid>
						<Grid size={{ xs: 12, md: 6 }}>
							<Typography variant="body2" color="text.secondary">
								{t("freelancerProposal.totalBudget")}
							</Typography>
							<Typography variant="body1" sx={{ fontWeight: 500 }}>
								{formatCurrency(proposal.totalBudget)}
							</Typography>
						</Grid>
					</Grid>
				</CardContent>
			</Card>

			{/* Relevant Experience */}
			<Card sx={{ mb: 3 }}>
				<CardContent>
					<Typography variant="h6" gutterBottom>
						{t("freelancerProposal.relevantExperienceTitle")}
					</Typography>
					<RichTextReadOnly
						content={proposal.relevantExperience}
						extensions={getExtensions()}
					/>
				</CardContent>
			</Card>

			{/* Project Strengths */}
			<Card sx={{ mb: 3 }}>
				<CardContent>
					<Typography variant="h6" gutterBottom>
						{t("freelancerProposal.projectStrengthsTitle")}
					</Typography>
					<RichTextReadOnly
						content={proposal.projectStrengths}
						extensions={getExtensions()}
					/>
				</CardContent>
			</Card>

			{/* Tasks and Pricing */}
			{proposal.tasks && proposal.tasks.length > 0 && (
				<Card sx={{ mb: 3 }}>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							{t("freelancerProposal.tasks")}
						</Typography>

						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>{t("freelancerProposal.task.description")}</TableCell>
										<TableCell align="right">
											{t("freelancerProposal.task.quantity")}
										</TableCell>
										<TableCell align="right">
											{t("freelancerProposal.task.unitPrice")}
										</TableCell>
										<TableCell align="right">
											{t("freelancerProposal.task.total")}
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{proposal.tasks.map(task => (
										<TableRow
											key={
												task.id ||
												`task-${task.description}-${task.quantity}-${task.unitPrice}`
											}
										>
											<TableCell>{task.description}</TableCell>
											<TableCell align="right">{task.quantity}</TableCell>
											<TableCell align="right">
												{formatCurrency(task.unitPrice)}
											</TableCell>
											<TableCell align="right">{formatCurrency(task.total)}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</CardContent>
				</Card>
			)}

			{/* External Costs */}
			{proposal.externalCosts && proposal.externalCosts.length > 0 && (
				<Card sx={{ mb: 3 }}>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							{t("freelancerProposal.externalCosts")}
						</Typography>

						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>
											{t("freelancerProposal.externalCost.description")}
										</TableCell>
										<TableCell align="right">
											{t("freelancerProposal.externalCost.estimatedBudget")}
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{proposal.externalCosts.map(cost => (
										<TableRow
											key={cost.id || `cost-${cost.description}-${cost.estimatedBudget}`}
										>
											<TableCell>{cost.description}</TableCell>
											<TableCell align="right">
												{formatCurrency(cost.estimatedBudget)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</CardContent>
				</Card>
			)}

			{/* Expenses */}
			{proposal.expenses && proposal.expenses.length > 0 && (
				<Card sx={{ mb: 3 }}>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							{t("freelancerProposal.expenses")}
						</Typography>

						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>{t("freelancerProposal.expense.nature")}</TableCell>
										<TableCell>{t("freelancerProposal.expense.description")}</TableCell>
										<TableCell align="right">
											{t("freelancerProposal.expense.estimatedBudget")}
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{proposal.expenses.map(expense => (
										<TableRow
											key={
												expense.id ||
												`expense-${expense.nature}-${expense.description}-${expense.estimatedBudget}`
											}
										>
											<TableCell>{t(`enums.expenseNature.${expense.nature}`)}</TableCell>
											<TableCell>{expense.description}</TableCell>
											<TableCell align="right">
												{formatCurrency(expense.estimatedBudget)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</CardContent>
				</Card>
			)}

			{/* Budget Summary */}
			<Card
				sx={{
					mb: 3,
					backgroundColor: zoullooColors.grey[25],
					border: `1px solid ${zoullooColors.grey[150]}`
				}}
			>
				<CardContent>
					<Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
						{t("freelancerProposal.budgetSummary")}
					</Typography>

					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mb: 1
						}}
					>
						<Typography variant="body1">{t("freelancerProposal.totalBudget")}</Typography>
						<Typography variant="body1" sx={{ fontWeight: 500 }}>
							{formatCurrency(proposal.totalBudget)}
						</Typography>
					</Box>

					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mb: 1
						}}
					>
						<Typography variant="body1">{t("freelancerProposal.zoullooFees")}</Typography>
						<Typography variant="body1" sx={{ fontWeight: 500 }}>
							{formatCurrency(calculateZoullooFees())}
						</Typography>
					</Box>

					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mb: 1
						}}
					>
						<Typography variant="body1">
							{t("freelancerProposal.totalExternalCosts")}
						</Typography>
						<Typography variant="body1" sx={{ fontWeight: 500 }}>
							{formatCurrency(proposal.totalExternalCosts)}
						</Typography>
					</Box>

					{/* External Costs Management Fees */}
					{project?.externalCostsFeePercentage &&
						project.externalCostsFeePercentage > 0 && (
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									mb: 1
								}}
							>
								<Typography variant="body1" sx={{ pl: 2, color: "text.secondary" }}>
									{t("freelancerProposal.externalCostsFees")} (
									{project.externalCostsFeePercentage}%)
								</Typography>
								<Typography variant="body1" sx={{ fontWeight: 500 }}>
									{formatCurrency(calculateExternalCostsFees())}
								</Typography>
							</Box>
						)}

					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mb: 1
						}}
					>
						<Typography variant="body1">
							{t("freelancerProposal.totalExpenses")}
						</Typography>
						<Typography variant="body1" sx={{ fontWeight: 500 }}>
							{formatCurrency(proposal.totalExpenses)}
						</Typography>
					</Box>

					{/* Expenses Management Fees */}
					{project?.expensesFeePercentage && project.expensesFeePercentage > 0 && (
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mb: 1
							}}
						>
							<Typography variant="body1" sx={{ pl: 2, color: "text.secondary" }}>
								{t("freelancerProposal.expensesFees")} ({project.expensesFeePercentage}%)
							</Typography>
							<Typography variant="body1" sx={{ fontWeight: 500 }}>
								{formatCurrency(calculateExpensesFees())}
							</Typography>
						</Box>
					)}

					{/* Processing Fees */}
					{project?.processingFees && project.processingFees > 0 && (
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mb: 1
							}}
						>
							<Typography variant="body1">{t("project.processingFees")}</Typography>
							<Typography variant="body1" sx={{ fontWeight: 500 }}>
								{formatCurrency(project.processingFees)}
							</Typography>
						</Box>
					)}

					<Divider sx={{ my: 1 }} />

					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mb: 1
						}}
					>
						<Typography variant="body1" sx={{ fontWeight: 600 }}>
							{t("freelancerProposal.grandTotal")}
						</Typography>
						<Typography variant="body1" sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
							{formatCurrency(
								calculateTotalWithFees() +
									calculateExternalCostsWithFees() +
									calculateExpensesWithFees() +
									(project?.processingFees || 0)
							)}
						</Typography>
					</Box>
				</CardContent>
			</Card>

			{/* Quote Download - Only for CSM/Admin when quote exists */}
			{isCSMorAdmin && proposal.pennylaneQuoteId && (
				<Card sx={{ mb: 3 }}>
					<CardContent>
						<Box display="flex" alignItems="center" justifyContent="space-between">
							<Typography variant="h6">
								{t("proposal.pennylaneQuote")}
							</Typography>
							<Button
								variant="contained"
								startIcon={isDownloadingQuote ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
								onClick={handleDownloadPennylaneQuote}
								disabled={isDownloadingQuote}
							>
								{t("proposal.downloadQuote")}
							</Button>
						</Box>
					</CardContent>
				</Card>
			)}

			{/* Documents */}
			<Card sx={{ mb: 3 }}>
				<CardContent>
					<Typography variant="h6" gutterBottom>
						{t("freelancerProposal.documents")}
					</Typography>

					{proposal.documents && proposal.documents.length > 0 ? (
						<List>
							{proposal.documents.map((document, idx) => (
								<ListItem
									key={
										document.id ||
										document.filePath ||
										document.urlPath ||
										`doc-${document.originalFileName}`
									}
									sx={{ pl: 0 }}
								>
									<ListItemIcon>
										<AttachFileIcon />
									</ListItemIcon>
									<ListItemText
										primary={
											<Link
												href={`${process.env.REACT_APP_ZOULLOO_FILE_STORAGE_BASE_URL}/${
													process.env
														.REACT_APP_FREELANCER_PROPOSAL_DOCUMENTS_CONTAINER_NAME
												}/${document.filePath || document.urlPath}`}
												target="_blank"
												rel="noopener noreferrer"
												sx={{ textDecoration: "none" }}
											>
												{document.originalFileName ||
													document.fileName ||
													`Document ${idx + 1}`}
											</Link>
										}
										secondary={document.contentType}
									/>
								</ListItem>
							))}
						</List>
					) : (
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ fontStyle: "italic" }}
						>
							{t("freelancerProposal.noDocuments")}
						</Typography>
					)}
				</CardContent>
			</Card>

			{/* Comments */}
			{proposal.comments && (
				<Card sx={{ mb: 3 }}>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							{t("freelancerProposal.comments")}
						</Typography>
						<RichTextReadOnly content={proposal.comments} extensions={getExtensions()} />
					</CardContent>
				</Card>
			)}

			{/* Workflow Actions - Priority: show first (not in draft mode) */}
			{canManageWorkflow && proposal.status !== FreelancerProposalStatus.NotSubmitted && (
				<Card sx={{ mb: 3 }}>
					<CardContent>
						<ProposalWorkflowActions
							proposalId={proposal.id}
							currentStatus={proposal.status}
							onActionComplete={handleActionComplete}
						/>
					</CardContent>
				</Card>
			)}

			{/* Conversation Thread - Only show if there are conversations or loading/error */}
			{(conversations.length > 0 || isLoadingConversations || conversationsError) && (
				<Card sx={{ mb: 3 }}>
					<CardContent>
						<ProposalConversationThread
							conversations={conversations}
							isLoading={isLoadingConversations}
							error={conversationsError}
						/>
					</CardContent>
				</Card>
			)}

			{/* Status History - Only for CSM/Admin */}
			{isCSMorAdmin && (
				<Accordion defaultExpanded sx={{ mb: 3 }}>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography variant="h6" display="flex" alignItems="center" gap={1}>
							<HistoryIcon />
							{t("proposal.statusHistory")}
						</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<ProposalStatusHistory proposalId={proposal.id} />
					</AccordionDetails>
				</Accordion>
			)}
		</Box>
	);
};

export default FreelancerProposalDetails;
